import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormikProps } from 'formik';
import { isFunction, isNull, map } from 'lodash';
import React, { useCallback } from 'react';

import {
  IModal,
  IModalFooterProps,
  IModalImagePreview,
  IModalProperties,
} from './modal-renderer';
import { Button } from '../button/button';
import { FormValues } from '../form/form';
import {
  CardHeader,
  DialogActions,
  DialogContent,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';

/** ModalInner only handles standard modal types — imagePreview is rendered directly in ModalRenderer. */
export type IModalStandard<TForm extends FormValues<TForm>> = Exclude<
  IModal<TForm>,
  IModalImagePreview
>;

interface IModalInnerPropsBase<TForm extends FormValues<TForm>> {
  modal: IModalStandard<TForm>;
  modalProps: IModalProperties;
  formikProps?: FormikProps<TForm>;
  /**
   * Whether the dialog is covering the whole screen. Only used for padding:
   * a fullscreen modal owns the viewport, so it has to clear the iOS home
   * indicator itself, and its 24px side gutters are a third of a phone's width.
   */
  fullScreen?: boolean;
}

interface IModalInnerPropsSave<TForm extends FormValues<TForm>>
  extends IModalInnerPropsBase<TForm> {
  formikProps: FormikProps<TForm>;
}

export type IModalInnerProps<TForm extends FormValues<TForm>> =
  | IModalInnerPropsBase<TForm>
  | IModalInnerPropsSave<TForm>;

export default function ModalInner<TForm extends FormValues<TForm>>({
  modal,
  modalProps,
  formikProps,
  fullScreen = false,
}: IModalInnerProps<TForm>) {
  const renderModalBody = useCallback(() => {
    if (isFunction(modal.body)) {
      if (
        (formikProps && modal.type === 'save') ||
        (formikProps && modal.type === 'readOnly')
      ) {
        return modal.body(formikProps);
      }
      if (modal.type === 'info') {
        return modal.body(modalProps);
      }

      throw new Error(`Modal type doesn't support a function body.`);
    }
    return modal.body;
  }, [formikProps, modal, modalProps]);

  const renderModalFooter = useCallback(() => {
    if (isFunction(modal.footer)) {
      // Cast footer to a known signature to resolve TypeScript's intersection
      // type inference across the modal union — safe because ModalInner is only
      // ever called with standard (non-imagePreview) modal types.
      const footerFn = modal.footer as (
        props: IModalFooterProps<TForm>,
      ) => React.ReactNode;
      return footerFn({ formikProps, modalProps });
    }
    return modal.footer;
  }, [formikProps, modal, modalProps]);

  return (
    <>
      {!modalProps.hideHeader && (
        <CardHeader
          // MUI's 16px all round is a lot of a phone's width for a title and a
          // close button; sm and up is unchanged.
          sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}
          title={modal.title}
          // A real heading, with the id the dialog is labelled by. CardHeader
          // renders its title as a <span> by default, which left the modal with
          // no heading for assistive tech (or a test) to find, and the dialog
          // itself with no accessible name. The variant — and so the look — is
          // CardHeader's own; only the element changes.
          slotProps={{ title: { component: 'h2', id: modalProps.titleId } }}
          avatar={!!modal.icon && <FontAwesomeIcon icon={modal.icon} />}
          action={
            <IconButton
              // "Close dialog", as the app's hand-rolled dialogs label theirs —
              // and not plain "Close", which several modals already use for a
              // footer button.
              aria-label="Close dialog"
              onClick={modalProps.onClose}
              disabled={modalProps.closeBlocked}
            >
              <Close />
            </IconButton>
          }
        />
      )}

      <DialogContent
        dividers
        autoFocus
        // MUI's dividers padding is `16px 24px`. Those 24px side gutters cost
        // 48px of a ~390px screen before a field draws, and they compound with
        // whatever the form inside adds — most visibly on a tab strip, whose
        // first tab then starts 40px in. sm and up keeps MUI's values exactly.
        sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1.5, sm: 2 } }}
      >
        {renderModalBody()}
      </DialogContent>
      <DialogActions
        sx={{
          // Only fullscreen needs this: it owns the viewport, so its action row
          // would otherwise sit under the iOS home indicator (and behind
          // Safari's toolbar). 8px is MUI's own padding, kept on top of the
          // inset. Matches multi-step-modal-renderer.
          ...(fullScreen && {
            px: 1.5,
            pb: 'calc(env(safe-area-inset-bottom) + 8px)',
          }),
        }}
      >
        {modal.footer
          ? renderModalFooter()
          : !isNull(modal.footer) && (
              <>
                {map(modalProps.buttons, (button, idx) => (
                  <Button key={idx} {...button} />
                ))}
              </>
            )}
      </DialogActions>
    </>
  );
}
